package com.zi.support.shared

/** One call, reduced to what the activity graph needs. [daysAgo] is 0 for today, in the viewer's time zone. */
data class CallPoint(val daysAgo: Int, val answered: Boolean, val durationSeconds: Long?)

/** Per-day totals for the calls graph. */
data class CallDay(val daysAgo: Int, val answered: Int, val missed: Int, val averageTalkSeconds: Long?) {
    val total: Int get() = answered + missed
}

/** One call, reduced to what the period insights need. [hour] is 0–23 in the viewer's time zone. */
data class CallFact(
    val daysAgo: Int,
    val hour: Int,
    val answered: Boolean,
    val durationSeconds: Long?,
    /** Caller's saved name, else the number; blank when withheld. */
    val caller: String,
    val takenBy: String,
    val linked: Boolean,
    val personal: Boolean,
    val outgoing: Boolean,
)

/** One day's totals from callStats/{team|uid}/days/{yyyy-mm-dd} (kept by the tallyCallStats function). */
data class CallStatDay(
    val daysAgo: Int,
    val answered: Int,
    val missed: Int,
    val talkSeconds: Long,
    /** Calls with talk time, for the average. */
    val talkCalls: Int,
    val incoming: Int,
    val outgoing: Int,
    val linked: Int,
    /** Personal and not linked. */
    val personal: Int,
    /** 24 buckets, midnight first. */
    val byHour: List<Int>,
    val callers: List<CallRank>,
    val team: List<CallRank>,
) {
    val total: Int get() = answered + missed
}


/** A caller or a support user with their call count. */
data class CallRank(val label: String, val count: Int, val answered: Int)

/** Everything the Calls overview shows for one period (week or month). */
data class CallInsights(
    val total: Int,
    val answered: Int,
    val missed: Int,
    /** Answered share of all calls, 0–100; 0 when there were none. */
    val answerRate: Int,
    val talkSeconds: Long,
    val averageTalkSeconds: Long?,
    val uniqueCallers: Int,
    val needsTicket: Int,
    val linked: Int,
    val personal: Int,
    val incoming: Int,
    val outgoing: Int,
    /** 24 buckets, midnight first. */
    val byHour: List<Int>,
    val busiestHour: Int?,
    val topCallers: List<CallRank>,
    val team: List<CallRank>,
    /** Calls in the period before this one, for the trend. */
    val previousTotal: Int,
)

/**
 * Call vocabulary and graph maths shared by web, Android and iOS, ported from the Call Log App.
 * Documents live in `supportCalls/{phoneId-startedAtMillis}`.
 */
object CallLogPolicy {
    const val COLLECTION: String = "supportCalls"
    /** On open, the Android app re-reads this much of the phone's call log. */
    const val SYNC_WINDOW_MILLIS: Long = 2L * 24 * 60 * 60 * 1000
    /** The header badge counts calls from this long ago that still need a ticket, the same 30 days as the "need a ticket" line on the Calls screen. */
    const val BADGE_WINDOW_MILLIS: Long = 30L * 24 * 60 * 60 * 1000
    /** History listens to this many newest calls; searches and filters ask the server for the rest. */
    const val LIST_LIMIT: Int = 100
    /** Most calls a filter (Needs ticket, Missed) loads. */
    const val FILTER_LIMIT: Int = 500
    /** Days of callStats the screens listen to: a month plus the month before it, for the trend. */
    const val STATS_DAYS: Int = 60
    val MISSED_OUTCOMES: List<String> = listOf("missed", "rejected", "no_answer")

    fun isAnswered(outcome: String): Boolean = outcome == "answered"

    fun outcomeLabel(outcome: String): String = when (outcome) {
        "answered" -> "Answered"
        "missed" -> "Missed"
        "rejected" -> "Declined"
        "no_answer" -> "No answer"
        else -> "Unknown"
    }

    fun shortDuration(seconds: Long): String = when {
        seconds < 60 -> "${seconds}s"
        seconds < 3600 -> "${seconds / 60}m ${seconds % 60}s"
        else -> "${seconds / 3600}h ${seconds % 3600 / 60}m"
    }

    /** A call still waiting for a ticket: not linked and not flagged personal by its receiver. */
    fun needsTicket(linkedTicketId: String?, personal: Boolean): Boolean = linkedTicketId.isNullOrBlank() && !personal

    /** Oldest day first, ending with today; [days] buckets. Calls outside the range are ignored. */
    fun dailyStats(calls: List<CallPoint>, days: Int): List<CallDay> {
        val byDay = calls.filter { it.daysAgo in 0 until days }.groupBy { it.daysAgo }
        return (days - 1 downTo 0).map { back ->
            val dayCalls = byDay[back].orEmpty()
            val talk = dayCalls.mapNotNull { it.durationSeconds }.filter { it > 0 }
            CallDay(
                daysAgo = back,
                answered = dayCalls.count { it.answered },
                missed = dayCalls.count { !it.answered },
                averageTalkSeconds = if (talk.isEmpty()) null else talk.sum() / talk.size,
            )
        }
    }

    /** The last [days] days (0 = today) and the same length before them, for the trend. */
    fun insights(calls: List<CallFact>, days: Int, top: Int = 5): CallInsights = insightsFromStats(statDays(calls), days, top)

    /** Individual calls bucketed the way the tallyCallStats function stores them. */
    fun statDays(calls: List<CallFact>): List<CallStatDay> = calls.groupBy { it.daysAgo }.map { (ago, list) ->
        fun rank(key: (CallFact) -> String) = list.filter { key(it).isNotBlank() }.groupBy(key)
            .map { (label, group) -> CallRank(label, group.size, group.count { it.answered }) }
        val talk = list.mapNotNull { it.durationSeconds }.filter { it > 0 }
        CallStatDay(ago, list.count { it.answered }, list.count { !it.answered }, talk.sum(), talk.size,
            list.count { !it.outgoing }, list.count { it.outgoing }, list.count { it.linked }, list.count { !it.linked && it.personal },
            List(24) { h -> list.count { it.hour == h } }, rank { it.caller }, rank { it.takenBy })
    }

    /** Per-day graph rows from stored totals: oldest first, ending with today, missing days as zero. */
    fun dailyFromStats(stats: List<CallStatDay>, days: Int): List<CallDay> {
        val byDay = stats.associateBy { it.daysAgo }
        return (days - 1 downTo 0).map { back ->
            val d = byDay[back]
            CallDay(back, d?.answered ?: 0, d?.missed ?: 0, d?.takeIf { it.talkCalls > 0 }?.let { it.talkSeconds / it.talkCalls })
        }
    }

    /** Totals re-dated so [lastDaysAgo] becomes day 0, for insights over a period that ends before today. */
    fun shiftStats(stats: List<CallStatDay>, lastDaysAgo: Int): List<CallStatDay> = stats.map { it.copy(daysAgo = it.daysAgo - lastDaysAgo) }

    /** The insights for the last [days] days from stored totals; the [days] before them give the trend. */
    fun insightsFromStats(stats: List<CallStatDay>, days: Int, top: Int = 5): CallInsights {
        val now = stats.filter { it.daysAgo in 0 until days }
        val answered = now.sumOf { it.answered }
        val total = now.sumOf { it.total }
        val talkSeconds = now.sumOf { it.talkSeconds }
        val talkCalls = now.sumOf { it.talkCalls }
        val linked = now.sumOf { it.linked }
        val personal = now.sumOf { it.personal }
        val byHour = List(24) { h -> now.sumOf { it.byHour.getOrElse(h) { 0 } } }
        fun merge(pick: (CallStatDay) -> List<CallRank>) = now.flatMap(pick).groupBy { it.label }
            .map { (label, list) -> CallRank(label, list.sumOf { it.count }, list.sumOf { it.answered }) }
            .filter { it.count > 0 }
            .sortedWith(compareByDescending<CallRank> { it.count }.thenBy { it.label.lowercase() })
        val callers = merge { it.callers }
        return CallInsights(
            total = total,
            answered = answered,
            missed = total - answered,
            answerRate = if (total == 0) 0 else (answered * 100 + total / 2) / total,
            talkSeconds = talkSeconds,
            averageTalkSeconds = if (talkCalls == 0) null else talkSeconds / talkCalls,
            uniqueCallers = callers.size,
            needsTicket = total - linked - personal,
            linked = linked,
            personal = personal,
            incoming = now.sumOf { it.incoming },
            outgoing = now.sumOf { it.outgoing },
            byHour = byHour,
            busiestHour = byHour.indices.maxByOrNull { byHour[it] }?.takeIf { byHour[it] > 0 },
            topCallers = callers.take(top),
            team = merge { it.team }.take(top),
            previousTotal = stats.filter { it.daysAgo in days until days * 2 }.sumOf { it.total },
        )
    }


    /** "09:00–10:00" for an hour bucket. */
    fun hourRange(hour: Int): String = "${pad(hour)}:00–${pad((hour + 1) % 24)}:00"

    private fun pad(n: Int) = if (n < 10) "0$n" else "$n"
}
